// Motor de bitácora UCI (Fase 4, Tanda 8a, ver PLAN.md) — `TablaConColumnas`:
// grilla fila×columnas de ancho FIJO (no horaria como `TablaHoraria24h`),
// para las tablas de procedimientos/exámenes/interconsultas que 113/114/115
// repiten con columnas propias (fecha de colocación/cambio/retiro, hora de
// solicitud/respuesta...) en vez de las 24 columnas horarias del turno.
// Mismo criterio de simplificación que el resto del motor: filas de
// catálogo fijo (procedimientos con nombre real) o filas numeradas en
// blanco (exámenes/interconsultas, cantidad fija — el papel tiene espacio
// para "algunas" filas, no una lista dinámica).
import type { JSX } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { leerSiNo, textoSiNo } from '../camposSimplesDatos';
import { fueraDeRango, type RangoEscala } from '../calculosClinicosDatos';

export interface ColumnaTablaConColumnas {
  readonly clave: string;
  readonly etiqueta: string;
  /** `siNo`: par de botones Sí / No con estado "sin responder" (se guarda `'si'`, `'no'` o `''`; en el `Par[]` sale como Sí / No). */
  readonly tipo?: 'texto' | 'fecha' | 'siNo' | 'opciones';
  /** Solo con `tipo: 'opciones'`: las casillas excluyentes del papel (p. ej. Isocoria / Anisocoria); tocar la elegida la deja "sin responder". */
  readonly opciones?: readonly string[];
  /**
   * Columna derivada de las demás de la MISMA fila (IMC, total de Glasgow, tiempo total...): se muestra de solo lectura con este valor y el mismo
   * valor sale en el resumen del `Par[]`; lo que hubiera guardado en la celda se ignora.
   */
  readonly calculada?: (fila: Readonly<Record<string, string>>) => string;
  /** Rango entero válido: fuera de él la celda se marca en rojo con un aviso, pero NO se bloquea el guardado (una celda vacía nunca se marca). */
  readonly rango?: RangoEscala;
}

/** Valor efectivo de una celda: el calculado si la columna lo define, o lo escrito. */
export function valorCeldaTablaConColumnas(estado: EstadoTablaConColumnas, filaClave: string, columna: ColumnaTablaConColumnas): string {
  const fila = estado[filaClave] ?? {};
  return columna.calculada ? columna.calculada(fila) : (fila[columna.clave] ?? '');
}

/** Texto de la celda para el `Par[]`: las columnas `siNo` salen como "Sí" / "No" (no como `si` / `no`). */
export function textoCeldaTablaConColumnas(estado: EstadoTablaConColumnas, filaClave: string, columna: ColumnaTablaConColumnas): string {
  const valor = valorCeldaTablaConColumnas(estado, filaClave, columna);
  return columna.tipo === 'siNo' ? textoSiNo(valor) : valor;
}

export interface FilaTablaConColumnas {
  readonly clave: string;
  readonly etiqueta: string;
}

export type EstadoTablaConColumnas = Readonly<Record<string, Readonly<Record<string, string>>>>;

function filaVacia(columnas: readonly ColumnaTablaConColumnas[]): Record<string, string> {
  return Object.fromEntries(columnas.map((c) => [c.clave, '']));
}

/** Estado inicial — una fila vacía (una celda en blanco por columna) por cada fila del catálogo. */
export function estadoInicialTablaConColumnas(filas: readonly FilaTablaConColumnas[], columnas: readonly ColumnaTablaConColumnas[]): EstadoTablaConColumnas {
  return Object.fromEntries(filas.map((f) => [f.clave, filaVacia(columnas)]));
}

/** Actualiza una sola celda (fila×columna) — pura, exportada para test, mismo criterio que `actualizarCeldaTablaHoraria24h`. */
export function actualizarCeldaTablaConColumnas(estado: EstadoTablaConColumnas, filaClave: string, columnaClave: string, valor: string): EstadoTablaConColumnas {
  return { ...estado, [filaClave]: { ...estado[filaClave], [columnaClave]: valor } };
}

/** Resumen compacto "columna:valor · columna:valor" de una fila, para el `Par[]` de respaldo — cadena vacía si la fila no tiene ningún dato. */
export function resumenFilaTablaConColumnas(estado: EstadoTablaConColumnas, filaClave: string, columnas: readonly ColumnaTablaConColumnas[]): string {
  return columnas
    .filter((c) => textoCeldaTablaConColumnas(estado, filaClave, c).trim() !== '')
    .map((c) => `${c.etiqueta}:${textoCeldaTablaConColumnas(estado, filaClave, c)}`)
    .join(' · ');
}

export interface TablaConColumnasProps {
  readonly filas: readonly FilaTablaConColumnas[];
  readonly columnas: readonly ColumnaTablaConColumnas[];
  readonly estado: EstadoTablaConColumnas;
  readonly onChange: (estado: EstadoTablaConColumnas) => void;
}

/** Grilla fila×columnas de ancho fijo — reusada por procedimientos/solicitud de exámenes/interconsultas de los 3 formularios UCI. */
export function TablaConColumnas({ filas, columnas, estado, onChange }: TablaConColumnasProps): JSX.Element {
  function actualizar(filaClave: string, columnaClave: string, valor: string): void {
    onChange(actualizarCeldaTablaConColumnas(estado, filaClave, columnaClave, valor));
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="border-collapse text-xs">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-hce-surface p-1 text-left font-normal text-hce-muted">
              <span className="sr-only">Fila</span>
            </th>
            {columnas.map((c) => (
              <th key={c.clave} className="p-1 text-left font-medium text-hce-ink">
                {c.etiqueta}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((fila) => (
            <tr key={fila.clave} className="border-t border-border">
              <td className="sticky left-0 z-10 bg-hce-surface whitespace-nowrap p-1 pr-2 font-medium text-hce-ink">{fila.etiqueta}</td>
              {columnas.map((c) => {
                const valor = valorCeldaTablaConColumnas(estado, fila.clave, c);
                const invalida = c.rango !== undefined && fueraDeRango(valor, c.rango);
                if (c.tipo === 'siNo') {
                  const elegido = leerSiNo(valor);
                  return (
                    <td key={c.clave} className="p-0.5">
                      <fieldset className="m-0 flex gap-1 border-0 p-0" aria-label={`${c.etiqueta} — fila ${fila.etiqueta}`}>
                        {(['si', 'no'] as const).map((v) => (
                          <Button key={v} type="button" size="sm" className="h-7 px-2 text-xs" variant={elegido === v ? 'default' : 'outline'} aria-pressed={elegido === v} onClick={() => actualizar(fila.clave, c.clave, elegido === v ? '' : v)}>
                            {v === 'si' ? 'Sí' : 'No'}
                          </Button>
                        ))}
                      </fieldset>
                    </td>
                  );
                }
                if (c.tipo === 'opciones' && c.opciones) {
                  return (
                    <td key={c.clave} className="p-0.5">
                      <fieldset className="m-0 flex gap-1 border-0 p-0" aria-label={`${c.etiqueta} — fila ${fila.etiqueta}`}>
                        {c.opciones.map((o) => (
                          <Button key={o} type="button" size="sm" className="h-7 px-2 text-xs" variant={valor === o ? 'default' : 'outline'} aria-pressed={valor === o} onClick={() => actualizar(fila.clave, c.clave, valor === o ? '' : o)}>
                            {o}
                          </Button>
                        ))}
                      </fieldset>
                    </td>
                  );
                }
                return (
                  <td key={c.clave} className="p-0.5">
                    <Input
                      className={`h-7 w-32 text-xs ${c.calculada ? 'bg-muted' : ''} ${invalida ? 'border-destructive' : ''}`}
                      type={c.tipo === 'fecha' ? 'date' : 'text'}
                      value={valor}
                      readOnly={c.calculada !== undefined}
                      aria-invalid={invalida || undefined}
                      title={invalida && c.rango ? `${c.rango.min}–${c.rango.max}` : undefined}
                      onChange={(e) => actualizar(fila.clave, c.clave, e.target.value)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
